/**
 * ONE SAVE BAR FOR A WHOLE PAGE OF TILLS.
 *
 * The order page draws a Payment types card on every POS pair it holds. Each
 * card used to keep its own drafts and its own sticky save bar, so an order
 * with three tills had three bars stacked at the bottom, each saving a third
 * of what had changed. The hub holds every till's draft (and the location's
 * grants, which every card on the page shares) and draws the one bar.
 *
 * A card inside a hub reads and writes through `usePaymentTypesHub()`. A card
 * used on its own wraps itself in a hub, so it still gets exactly one bar.
 *
 * Broadcast ("apply to other tills") needs one source: it is offered when
 * exactly one till on the page has changes, and those changes can be copied.
 */
import React, { createContext, useCallback, useContext, useMemo, useRef, useState } from 'react';
import { toastService } from '../../../services/toastService';
import {
  RegisterState, GrantState, grantsFromGateway, buildRegisterPatch, buildGrantPatch,
  buildBulkFields, countChanges, isSurchargeApproved,
} from './model';
import { SaveBar, ErrorBanners, BroadcastSummary, useBroadcast, type BulkResult } from './SaveBar';
import { saveChanges } from './save';
import { LocationValuesCard } from './RegisterPaymentTypes';
import { useUnsavedChangesGuard } from './UnsavedChangesGuard';

const NO_GRANTS: GrantState = { tipsEnabled: false, cashTipsEnabled: false, surchargeEnabled: false };

export interface PaymentTypesHub {
  base: Record<string, RegisterState>;
  draft: Record<string, RegisterState>;
  setTillDraft: (terminalId: string, next: RegisterState) => void;
  /** A fresh read of some tills. A till with unsaved edits keeps its draft. */
  ingest: (rows: Record<string, RegisterState>, names: Record<string, string>) => void;
  gateway: any | null;
  /** A fresh read of the location's gateway row. Unsaved grant edits are kept. */
  ingestGateway: (gw: any | null) => void;
  grants: GrantState;
  setGrants: (next: GrantState) => void;
  saving: boolean;
  /** A card mounts → the bar is drawn; the last one unmounts → it goes. */
  attach: () => () => void;
}

const HubContext = createContext<PaymentTypesHub | null>(null);

export const usePaymentTypesHub = () => useContext(HubContext);

export const PaymentTypesSaveHub: React.FC<{
  tenantId: string;
  locationId?: string;
  locationName?: string;
  tenantName?: string;
  /** After anything saved — the page re-reads its setup state. */
  onSaved?: () => void;
  /** Draw the merchant's location values once, under every till. */
  showLocationValues?: boolean;
  children: React.ReactNode;
}> = ({ tenantId, locationId, locationName, tenantName, onSaved, showLocationValues, children }) => {
  const [base, setBase] = useState<Record<string, RegisterState>>({});
  const [draft, setDraft] = useState<Record<string, RegisterState>>({});
  const [names, setNames] = useState<Record<string, string>>({});
  const [gateway, setGateway] = useState<any | null>(null);
  const [baseGrants, setBaseGrants] = useState<GrantState>(NO_GRANTS);
  const [grants, setGrants] = useState<GrantState>(NO_GRANTS);
  const [saving, setSaving] = useState(false);
  const [saveErrors, setSaveErrors] = useState<string[]>([]);
  const [bulkDone, setBulkDone] = useState<BulkResult[] | null>(null);
  const [attached, setAttached] = useState(0);

  const baseRef = useRef(base); baseRef.current = base;
  const grantsDirtyRef = useRef(false);

  const ingest = useCallback((rows: Record<string, RegisterState>, nm: Record<string, string>) => {
    setNames((n) => ({ ...n, ...nm }));
    setDraft((d) => {
      const out = { ...d };
      for (const [id, b] of Object.entries(rows)) {
        const prevBase = baseRef.current[id];
        const prevDraft = d[id];
        out[id] = prevDraft && prevBase && buildRegisterPatch(prevBase, prevDraft) ? prevDraft : b;
      }
      return out;
    });
    setBase((b) => ({ ...b, ...rows }));
  }, []);

  const ingestGateway = useCallback((gw: any | null) => {
    const g = grantsFromGateway(gw);
    setGateway(gw ?? null);
    setBaseGrants(g);
    if (!grantsDirtyRef.current) setGrants(g);
  }, []);

  const setTillDraft = useCallback((id: string, next: RegisterState) => setDraft((d) => ({ ...d, [id]: next })), []);
  const attach = useCallback(() => { setAttached((n) => n + 1); return () => setAttached((n) => n - 1); }, []);

  // ── Dirty state across every till on the page ──────────────────────────
  const patches = Object.fromEntries(Object.keys(draft).map((id) => [id, base[id] ? buildRegisterPatch(base[id], draft[id]) : null]));
  const grantPatch = buildGrantPatch(baseGrants, grants);
  grantsDirtyRef.current = !!grantPatch;
  const count = Object.values(patches).reduce((n, p) => n + countChanges(p, null), 0) + countChanges(null, grantPatch);
  const guard = useUnsavedChangesGuard(count > 0);

  const dirtyTills = Object.entries(patches).filter(([, p]) => !!p).map(([id]) => id);
  const sourceId = dirtyTills.length === 1 && locationId ? dirtyTills[0] : null;
  const sourceFields = sourceId ? buildBulkFields(base[sourceId], draft[sourceId]) : null;
  const broadcast = useBroadcast({
    tenantId,
    source: sourceId && sourceFields ? { terminalId: sourceId, locationId: locationId! } : null,
    fields: sourceFields,
  });

  const discard = () => { setDraft(base); setGrants(baseGrants); setSaveErrors([]); broadcast.reset(); };

  const save = async () => {
    setSaving(true);
    setSaveErrors([]);
    setBulkDone(null);
    const wide = broadcast.available && broadcast.scope !== 'one' && sourceId;
    const outcome = await saveChanges({
      tenantId,
      locationId,
      locationName,
      grantPatch,
      registers: Object.entries(patches).map(([terminalId, patch]) => ({ terminalId, name: names[terminalId] || terminalId, patch })),
      broadcast: wide ? { sourceTerminalId: sourceId!, run: broadcast.run } : null,
    });
    if (outcome.grantsSaved) setBaseGrants(grants);
    if (outcome.savedTerminals.length) {
      setBase((b) => {
        const next = { ...b };
        for (const id of outcome.savedTerminals) next[id] = draft[id];
        return next;
      });
    }
    if (outcome.bulkResults) setBulkDone(outcome.bulkResults);
    setSaveErrors(outcome.errors);
    if (!outcome.errors.length) {
      broadcast.reset();
      toastService.success('Saved. The till picks it up now — one that is offline does when it reconnects.');
    }
    if (outcome.grantsSaved || outcome.savedTerminals.length) onSaved?.();
    setSaving(false);
  };

  const value = useMemo<PaymentTypesHub>(() => ({
    base, draft, setTillDraft, ingest, gateway, ingestGateway, grants, setGrants, saving, attach,
  }), [base, draft, setTillDraft, ingest, gateway, ingestGateway, grants, saving, attach]);

  return (
    <HubContext.Provider value={value}>
      {children}
      {attached > 0 && showLocationValues && locationId && gateway && (
        <div className="mt-3">
          <LocationValuesCard
            gateway={gateway}
            locationName={locationName || 'this location'}
            approved={isSurchargeApproved(gateway, grants)}
          />
        </div>
      )}
      {attached > 0 && (
        <div className="mt-3">
          <ErrorBanners errors={saveErrors} onDismiss={(i) => setSaveErrors((e) => e.filter((_, j) => j !== i))} />
          {bulkDone && <BroadcastSummary results={bulkDone} onDismiss={() => setBulkDone(null)} />}
          <SaveBar
            count={count}
            saving={saving}
            onSave={save}
            onDiscard={discard}
            broadcast={broadcast}
            locationName={locationName}
            tenantName={tenantName}
            locationWide={!!grantPatch}
          />
        </div>
      )}
      {guard.dialog}
    </HubContext.Provider>
  );
};
